import { useMemo } from 'react';
import { approvalCommentApi } from '@/constant/apiURL/approval-comment';
import {
  deleteDataFetcher,
  postDataFetcher,
  putDataFetcher,
} from '@/utils/apis';

/**
 * 품의(approval) 코멘트 mutation 함수들
 *
 *  - POST   /api/v2/approval/{documentId}/comments (작성)  ← 201 응답이 갱신된 스레드 전체
 *  - PUT    /api/v2/approval/{documentId}/comments/{commentId}           (수정)
 *  - DELETE /api/v2/approval/{documentId}/comments/{commentId}           (삭제)
 *  - PUT    /api/v2/approval/{documentId}/comments/{commentId}/recommend (좋아요, recommend: true=좋아요, false=취소)
 */

export type ApprovalCommentAttachment = { name: string; link: string };

export type CreateApprovalCommentPayload = {
  content: string;
  mentions: string[];
  attachments: ApprovalCommentAttachment[];
};

export type UpdateApprovalCommentPayload = {
  commentId: string;
  content: string;
  mentions: string[];
  attachments: ApprovalCommentAttachment[];
};

/** 멘션 식별자는 24자리 hex passportId 만 허용 — 어긋나면 서버가 422. */
const MENTION_PASSPORT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

/**
 * 형식이 어긋난 멘션 식별자를 걸러낸다. 과거엔 통과했지만 멘션 알림만 누락됐으므로
 * 걸러내도 사용자 관점 동작은 동일하고, 등록 자체가 422 로 실패하는 것만 막힌다.
 * (V1 포맷 멘션 노드처럼 passportId 가 아닌 값이 섞일 수 있는 경로 대비)
 */
export const sanitizeMentionIds = (ids: string[]): string[] =>
  ids.filter((id) => MENTION_PASSPORT_ID_PATTERN.test(id));

/**
 * 코멘트 작성. 문서번호로 호출하므로 그룹 ID 를 들고 있을 필요가 없다.
 * @returns 갱신된 스레드 전체 envelope — 호출처가 재조회 없이 이 값으로 리스트를 교체.
 */
const createApprovalComment = async (
  documentId: string,
  payload: CreateApprovalCommentPayload,
): Promise<unknown> => {
  const mentions = sanitizeMentionIds(payload.mentions);
  return postDataFetcher(approvalCommentApi.COMMENTS(documentId), {
    content: payload.content,
    attachments: payload.attachments,
    // 서버가 mentions 를 required 로 검증해 빈 배열을 422 로 거절 — 멘션 없으면 키를 빼서 보낸다.
    ...(mentions.length > 0 ? { mentions } : {}),
  });
};

const updateApprovalComment = async (
  documentId: string,
  payload: UpdateApprovalCommentPayload,
): Promise<void> => {
  await putDataFetcher(
    approvalCommentApi.DETAIL(documentId, payload.commentId),
    {
      content: payload.content,
      mentions: sanitizeMentionIds(payload.mentions),
      attachments: payload.attachments,
    },
  );
};

const deleteApprovalComment = async (
  documentId: string,
  commentId: string,
): Promise<void> => {
  await deleteDataFetcher(approvalCommentApi.DETAIL(documentId, commentId));
};

const likeApprovalComment = async (
  documentId: string,
  commentId: string,
  isLike: boolean,
): Promise<void> => {
  await putDataFetcher(approvalCommentApi.RECOMMEND(documentId, commentId), {
    recommend: isLike,
  });
};

export type UseApprovalCommentMutationsResult = {
  createApprovalComment: typeof createApprovalComment;
  updateApprovalComment: typeof updateApprovalComment;
  deleteApprovalComment: typeof deleteApprovalComment;
  likeApprovalComment: typeof likeApprovalComment;
};

/**
 * 안정적인 reference 로 4개 함수 묶음 반환. 모두 documentId 를 앞에 받으므로
 * 호출처가 바인딩해서 portal CommentInput/CommentItem 의 `*Override` prop 에 넘긴다.
 */
const useApprovalCommentMutations = (): UseApprovalCommentMutationsResult => {
  return useMemo(
    () => ({
      createApprovalComment,
      updateApprovalComment,
      deleteApprovalComment,
      likeApprovalComment,
    }),
    [],
  );
};

export {
  createApprovalComment,
  updateApprovalComment,
  deleteApprovalComment,
  likeApprovalComment,
};

export default useApprovalCommentMutations;
